// ============================================
// LIGA AJUPAM - Cortes de clasificación por zona (tabla pública)
// ============================================
// Qué le pasa a un equipo según su puesto en la zona:
//   · hasta `direct`            → pasa directo a la fase final
//   · de `direct+1` a `total`   → va al repechaje
//   · después de `total`        → no clasifica
//
// Los números salen de los mismos campos que usa `_zone_cuts` en la base:
//   total  = league_settings.teams_qualifying_to_finals
//   direct = tournament_categories.direct_to_finals_per_zone (override de la
//            categoría) o, si es nulo, league_settings.default_direct_to_finals_per_zone.
// Un `direct` de 0, negativo o mayor que `total` no distingue directos de
// repechaje (igual que `_zone_cuts`, que lo vuelve nulo).
//
// Sin `new Date` ni dependencias de zona horaria: acá solo hay números.
// Banco de pruebas: `npm run test:zone-cuts`.

export interface ZoneCuts {
  /** Puestos que pasan directo. `null` si la categoría no distingue directos. */
  direct: number | null;
  /** Puestos que clasifican en total (directos + repechaje). */
  total: number;
}

export type ZoneStatus = 'directo' | 'repechaje' | 'clasifica' | 'no_clasifica';

const isPosInt = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

/**
 * Normaliza los valores crudos de la base a un `ZoneCuts`, o `null` si falta el
 * total (sin total no hay corte que mostrar).
 */
export const resolveZoneCuts = (
  categoryDirect: number | null | undefined,
  defaultDirect: number | null | undefined,
  total: number | null | undefined
): ZoneCuts | null => {
  if (!isPosInt(total)) return null;
  const rawDirect = categoryDirect ?? defaultDirect;
  const direct = isPosInt(rawDirect) && rawDirect <= total ? rawDirect : null;
  return { direct, total };
};

/**
 * Estado de un puesto. Con `direct` nulo, los que clasifican se marcan
 * `clasifica` (sin repechaje que los separe de los directos).
 */
export const zoneStatusForPosition = (position: number, cuts: ZoneCuts): ZoneStatus => {
  if (position > cuts.total) return 'no_clasifica';
  if (cuts.direct === null) return 'clasifica';
  return position <= cuts.direct ? 'directo' : 'repechaje';
};

export const ZONE_STATUS_LABEL: Record<ZoneStatus, string> = {
  directo: 'Pasan directo',
  repechaje: 'Repechaje',
  clasifica: 'Clasifican',
  no_clasifica: 'No clasifican',
};

/** Texto de rango para la leyenda: «1°», «1°–4°». */
export const formatPositionRange = (from: number, to: number): string =>
  from === to ? `${from}°` : `${from}°–${to}°`;
